import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Home, Search as SearchIcon, MapPin, Bed, Bath, Square, DollarSign, Eye, Shield, Waves } from "lucide-react";
import { toast } from "sonner";
import TexasPrideBanner from "../components/TexasPrideBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SEO keyword variations - rotates on each page load for better organic reach
const SEO_VARIATIONS = [
  {
    tagline: "No Agent or Broker Listings • Buyers Save, Sellers Save",
    location: "DFW Metroplex"
  },
  {
    tagline: "No Real Estate Agents or Brokers • Direct from Owners",
    location: "Dallas Fort Worth Metroplex"
  },
  {
    tagline: "Zero Agent Fees • No Broker Commissions",
    location: "DFW"
  },
  {
    tagline: "No Real Estate Agent Listings • Save Thousands",
    location: "Dallas-Fort Worth Metro Area"
  },
  {
    tagline: "Agent-Free Listings • No Broker Competition",
    location: "Dallas Fort Worth Metroplex"
  },
  {
    tagline: "No Real Estate Agents or Brokers • FSBO Only",
    location: "DFW Metro"
  }
];

const Search = ({ user }) => {
  // Select random variation on component mount for SEO diversity
  const [seoVariation] = useState(() => 
    SEO_VARIATIONS[Math.floor(Math.random() * SEO_VARIATIONS.length)]
  );
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    city: '',
    county: '',
    zip_code: '',
    radius_miles: '',
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
    bathrooms: '',
    propertyType: '',
    garage_spaces: '',
    garage_type: '',
    has_pool: '',
    pool_type: '',
    sort: 'newest'
  });
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    // Check auth first - if not logged in, redirect to register with city param
    if (!user) {
      const urlParams = new URLSearchParams(window.location.search);
      const cityParam = urlParams.get('city');
      if (cityParam) {
        navigate(`/register?role=buyer&city=${cityParam}`);
      } else {
        navigate('/register?role=buyer');
      }
      return;
    }
    
    // Check for URL parameters on page load
    const urlParams = new URLSearchParams(window.location.search);
    const cityParam = urlParams.get('city');
    const countyParam = urlParams.get('county');
    
    if (cityParam || countyParam) {
      setFilters(prev => ({
        ...prev,
        city: cityParam || '',
        county: countyParam || ''
      }));
    }
    
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      // Check for URL parameters to pre-filter listings
      const urlParams = new URLSearchParams(window.location.search);
      const cityParam = urlParams.get('city');
      const countyParam = urlParams.get('county');
      
      // Build query string for listings
      let listingsUrl = `${API}/listings`;
      if (cityParam || countyParam) {
        const params = new URLSearchParams();
        if (cityParam) params.append('city', cityParam);
        if (countyParam) params.append('county', countyParam);
        listingsUrl += `?${params.toString()}`;
      }
      
      const [listingsRes, countiesRes, citiesRes] = await Promise.all([
        axios.get(listingsUrl),
        axios.get(`${API}/data/counties`),
        axios.get(`${API}/data/cities`)
      ]);
      setListings(listingsRes.data);
      setCounties(countiesRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const searchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.city) params.append('city', filters.city);
      if (filters.county) params.append('county', filters.county);
      if (filters.zip_code) params.append('zip_code', filters.zip_code);
      if (filters.radius_miles) params.append('radius_miles', filters.radius_miles);
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      if (filters.bedrooms) params.append('bedrooms', filters.bedrooms);
      if (filters.bathrooms) params.append('bathrooms', filters.bathrooms);
      if (filters.propertyType) params.append('property_type', filters.propertyType);
      if (filters.garage_spaces) params.append('garage_spaces', filters.garage_spaces);
      if (filters.garage_type) params.append('garage_type', filters.garage_type);
      if (filters.has_pool) params.append('has_pool', filters.has_pool);
      if (filters.pool_type) params.append('pool_type', filters.pool_type);
      params.append('sort', filters.sort);

      const response = await axios.get(`${API}/listings?${params.toString()}`);
      setListings(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-slate-50">
      <Helmet>
        <title>Search FSBO Homes in DFW | Its-Sold.com</title>
        <meta name="description" content="Browse free For Sale By Owner homes in Dallas-Fort Worth. Filter by city, price, bedrooms. No agent fees, no commissions." />
        <link rel="canonical" href="https://its-sold.com/search" />
      </Helmet>
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-orange-100 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-1.5 sm:space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-lg sm:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <div className="flex items-center space-x-1.5 sm:space-x-3">
              <Button onClick={() => navigate('/buyer-resources')} variant="ghost" className="text-xs sm:text-sm px-2 sm:px-4 hidden lg:flex">
                <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Buyer Protection
              </Button>
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className="bg-orange-600 hover:bg-orange-700 text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2" data-testid="nav-dashboard-btn">
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button onClick={() => navigate('/login')} variant="ghost" className="text-gray-700 hover:text-orange-600 text-xs sm:text-sm px-2 sm:px-3" data-testid="nav-login-btn">
                    Login
                  </Button>
                  <Button onClick={() => navigate('/register?role=seller')} className="bg-orange-600 hover:bg-orange-700 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 hidden md:flex">
                    Seller Sign Up
                  </Button>
                  <Button onClick={() => navigate('/register?role=buyer')} className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 hidden md:flex">
                    Buyer Sign Up
                  </Button>
                  {/* Mobile: Combined Sign Up */}
                  <Button onClick={() => navigate('/register')} className="bg-orange-600 hover:bg-orange-700 text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 md:hidden">
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-16 sm:pt-24 pb-8 sm:pb-12 px-3 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Search Header - Mobile Optimized */}
          <div className="text-center mb-6 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Find Your Dream Home
            </h1>
            <p className="text-base sm:text-xl text-gray-700 mb-1 sm:mb-2 font-semibold">For Sale By Owner (FSBO) Listings ONLY</p>
            <p className="text-sm sm:text-lg text-gray-600 mb-1 sm:mb-2">{seoVariation.tagline}</p>
            <p className="text-base text-gray-500">A Free Marketplace for Home Selling across the {seoVariation.location}</p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 mb-6 sm:mb-8 border border-orange-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Find Your Perfect Home</h3>
              <Select value={filters.sort} onValueChange={(val) => setFilters({...filters, sort: val})}>
                <SelectTrigger className="w-full sm:w-48" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                  <SelectItem value="sqft">Largest Sqft</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Location Filters */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Location</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <Input
                  placeholder="ZIP Code"
                  value={filters.zip_code}
                  onChange={(e) => setFilters({...filters, zip_code: e.target.value})}
                  data-testid="filter-zip"
                />
                <Select value={filters.radius_miles} onValueChange={(val) => setFilters({...filters, radius_miles: val})}>
                  <SelectTrigger data-testid="filter-radius">
                    <SelectValue placeholder="Radius" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">Within 5 miles</SelectItem>
                    <SelectItem value="10">Within 10 miles</SelectItem>
                    <SelectItem value="15">Within 15 miles</SelectItem>
                    <SelectItem value="25">Within 25 miles</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.city} onValueChange={(val) => setFilters({...filters, city: val})}>
                <SelectTrigger data-testid="filter-city">
                  <SelectValue placeholder="City" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.county} onValueChange={(val) => setFilters({...filters, county: val})}>
                <SelectTrigger data-testid="filter-county">
                  <SelectValue placeholder="County" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Counties</SelectItem>
                  {counties.map(county => (
                    <SelectItem key={county} value={county}>{county}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            </div>

            {/* Property Details */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Property Details</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <Select value={filters.bedrooms} onValueChange={(val) => setFilters({...filters, bedrooms: val})}>
                  <SelectTrigger data-testid="filter-bedrooms">
                    <SelectValue placeholder="Beds" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Beds</SelectItem>
                    {[1,2,3,4,5].map(num => (
                      <SelectItem key={num} value={num.toString()}>{num}+ Beds</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filters.bathrooms} onValueChange={(val) => setFilters({...filters, bathrooms: val})}>
                  <SelectTrigger data-testid="filter-bathrooms">
                    <SelectValue placeholder="Baths" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Baths</SelectItem>
                    {[1, 1.5, 2, 2.5, 3, 3.5, 4].map(num => (
                      <SelectItem key={num} value={num.toString()}>{num}+ Baths</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filters.garage_spaces} onValueChange={(val) => setFilters({...filters, garage_spaces: val})}>
                  <SelectTrigger data-testid="filter-garage-spaces">
                    <SelectValue placeholder="Garage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Garage</SelectItem>
                    <SelectItem value="1">1-Car</SelectItem>
                    <SelectItem value="2">2-Car</SelectItem>
                    <SelectItem value="3">3-Car+</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.garage_type} onValueChange={(val) => setFilters({...filters, garage_type: val})}>
                  <SelectTrigger data-testid="filter-garage-type">
                    <SelectValue placeholder="Garage Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Type</SelectItem>
                    <SelectItem value="Attached">Attached</SelectItem>
                    <SelectItem value="Detached">Detached</SelectItem>
                    <SelectItem value="Carport">Carport</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.propertyType} onValueChange={(val) => setFilters({...filters, propertyType: val})}>
                  <SelectTrigger data-testid="filter-property-type">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="Single Family">Single Family</SelectItem>
                    <SelectItem value="Townhouse">Townhouse</SelectItem>
                    <SelectItem value="Condo">Condo</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.has_pool} onValueChange={(val) => setFilters({...filters, has_pool: val})}>
                  <SelectTrigger data-testid="filter-has-pool" className="bg-blue-50 border-blue-200">
                    <SelectValue placeholder="🏊 Pool" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Pool</SelectItem>
                    <SelectItem value="true">Has Pool</SelectItem>
                    <SelectItem value="false">No Pool</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.pool_type} onValueChange={(val) => setFilters({...filters, pool_type: val})}>
                  <SelectTrigger data-testid="filter-pool-type" className="bg-blue-50 border-blue-200">
                    <SelectValue placeholder="Pool Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="Inground Gunite">Inground Gunite</SelectItem>
                    <SelectItem value="Inground Vinyl">Inground Vinyl</SelectItem>
                    <SelectItem value="Above Ground">Above Ground</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Price Range */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Price Range</h4>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  placeholder="Min Price"
                  value={filters.minPrice}
                  onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
                  data-testid="filter-min-price"
                />
                <Input
                  type="number"
                  placeholder="Max Price"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
                  data-testid="filter-max-price"
                />
              </div>
            </div>

            {/* Search Button */}
            <div className="mt-6 flex items-center space-x-3">
              <Button 
                onClick={searchListings} 
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-lg py-6" 
                data-testid="search-btn"
              >
                <SearchIcon className="w-5 h-5 mr-2" />
                Search Properties
              </Button>
              <Button 
                onClick={() => {
                  setFilters({city: '', county: '', zip_code: '', radius_miles: '', minPrice: '', maxPrice: '', bedrooms: '', bathrooms: '', propertyType: '', garage_spaces: '', garage_type: '', has_pool: '', pool_type: '', sort: 'newest'});
                  fetchData();
                }}
                variant="outline"
                data-testid="clear-filters-btn"
              >
                Clear All
              </Button>
            </div>
          </div>

          {/* Results Count + Seller CTA */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-gray-600">
              <span className="font-semibold text-orange-600">{listings.length}</span> properties found
            </p>
            {user && (
              <Button 
                onClick={() => navigate('/create')} 
                className="bg-green-600 hover:bg-green-700"
                data-testid="list-your-home-cta"
              >
                🎯 List Your Home - Limited Competition!
              </Button>
            )}
          </div>

          {/* Listings Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-white rounded-xl overflow-hidden border border-gray-200">
                  <div className="h-48 bg-gray-200 skeleton"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-6 bg-gray-200 rounded skeleton"></div>
                    <div className="h-4 bg-gray-200 rounded skeleton w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-24 h-24 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
                <SearchIcon className="w-12 h-12 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>No For Sale By Owner listings found</h3>
              <p className="text-gray-600 mb-6">There are no For Sale By Owner listings found in your search results. Try adjusting your search filters or broaden your search area.</p>
              <Button onClick={() => setFilters({ city: '', county: '', zip_code: '', radius_miles: '', minPrice: '', maxPrice: '', bedrooms: '', bathrooms: '', propertyType: '', garage_spaces: '', garage_type: '', has_pool: '', pool_type: '', sort: 'newest' })} variant="outline" data-testid="clear-filters-btn">
                Clear All Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {listings.map((listing) => (
                <div
                  key={listing.id}
                  onClick={() => navigate(`/listing/${listing.id}`)}
                  className="bg-white rounded-xl overflow-hidden border border-gray-200 hover-lift cursor-pointer"
                  data-testid={`listing-card-${listing.id}`}
                >
                  <div className="relative h-44 sm:h-48 bg-gray-200 img-zoom">
                    {listing.photos && listing.photos.length > 0 ? (
                      <img
                        src={listing.photos[0]}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/800x600/f97316/FFFFFF?text=No+Image';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-100 to-orange-200">
                        <Home className="w-12 h-12 sm:w-16 sm:h-16 text-orange-400" />
                      </div>
                    )}
                    {listing.is_seed_listing && (
                      <div className="absolute top-2 left-2 sm:top-3 sm:left-3 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold shadow-lg bg-blue-500 text-white">
                        📋 DEMO
                      </div>
                    )}
                    {listing.tier !== 'free' && (
                      <div className={`absolute top-2 right-2 sm:top-3 sm:right-3 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold shadow-lg ${
                        listing.tier === 'featured' 
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white' 
                          : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white'
                      }`}>
                        {listing.tier === 'featured' ? '⭐ FEATURED' : '🏆 PREMIUM'}
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 bg-white/90 backdrop-blur-sm px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg flex items-center space-x-0.5 sm:space-x-1 text-[10px] sm:text-xs">
                      <Eye className="w-3 h-3 text-gray-600" />
                      <span>{listing.views || 0}</span>
                    </div>
                  </div>
                  <div className="p-3 sm:p-5">
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <span className="text-xl sm:text-2xl font-bold text-orange-600" style={{ fontFamily: 'Playfair Display' }}>
                        ${listing.price.toLocaleString()}
                      </span>
                      {listing.suggested_price && listing.price && Math.abs(listing.price - listing.suggested_price) / listing.price > 0.05 && (
                        <span className="text-[10px] sm:text-xs text-gray-500">
                          Est. ${listing.suggested_price.toLocaleString()}
                        </span>
                      )}
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2 line-clamp-1">{listing.title}</h3>
                    <div className="flex items-center text-sm text-gray-600 mb-3">
                      <MapPin className="w-4 h-4 mr-1" />
                      <span className="line-clamp-1">{listing.city}, {listing.county}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-700">
                        <div className="flex items-center">
                          <Bed className="w-4 h-4 mr-1" />
                          <span>{listing.bedrooms}</span>
                        </div>
                        <div className="flex items-center">
                          <Bath className="w-4 h-4 mr-1" />
                          <span>{listing.bathrooms}</span>
                        </div>
                        <div className="flex items-center">
                          <Square className="w-4 h-4 mr-1" />
                          <span>{listing.sqft.toLocaleString()}</span>
                        </div>
                      </div>
                      {listing.has_pool && (
                        <div className="flex items-center text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                          <Waves className="w-3 h-3 mr-1" />
                          <span>Pool</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;